import {Component, inject, Input, OnInit} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {CmsApiService} from '../../core/cms-api.service';
import {AConst} from '../../core/a-const.enum';
import {SelectionModel} from "@angular/cdk/collections";
import {KulturNavOption, KulturNavParams} from '../../core/definitions/kultur-nav/kultur-nav-params';
import {KulturNavImportParameters} from "../../core/definitions/kultur-nav/kultur-nav-import-parameters";

export interface KulturNavConceptElement {
  selected: boolean;
  conceptName: string;
  dataSet: string;
  uuid: string;
}

@Component({
  selector: 'app-kultur-nav-importer-items',
  templateUrl: './kultur-nav-importer-items.component.html',
  styleUrls: ['./kultur-nav-importer-items.component.scss'],
  standalone: false
})
export class KulturNavImporterItemsComponent implements OnInit {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);

  @Input() dialogRef: MatDialogRef<any>;
  @Input() kulturNavData: KulturNavParams;


  hasSelections = false;
  kulturNavConceptDataSource: KulturNavConceptElement[] = [];
  conceptColumns: string[] = ['selected', 'conceptName', 'dataSet'];
  searching = false;
  importing = false;
  noResults = false;
  errors = [];
  importedItems: any[] = [];
  selectedItems: any[] = [];
  initQuery = '';
  selectedOption: KulturNavOption;
  options: KulturNavOption[];
  selection: SelectionModel<KulturNavConceptElement>;
  private selectedSuperobjectFilters = [];

  ngOnInit(): void {
    // This will enable the possibility to choose between single and multiple selection mode
    // Must add a variable to differentiate when to use when (backend)
    this.selection = new SelectionModel(this.kulturNavData.multi_import, [])
    this.initQuery = this.kulturNavData.phrase || '';
    if (this.kulturNavData.sub_type && this.kulturNavData.sub_type.options) {
      this.options = this.kulturNavData.sub_type.options.sort(this.cultureDataOptionsSort);
      for (const option of this.kulturNavData.sub_type.options) {
        if (option.selected) {
          this.selectedOption = JSON.parse(JSON.stringify(option));
        }
      }
    }
    if (this.initQuery) {
      if (this.initQuery.indexOf('*') === -1) {
        this.initQuery += '*';
      }
      this.runSearch(this.initQuery).then();
    }
  }

  async runImport() {
    this.importing = true;
    this.errors = [];
    for (const element of this.selection.selected) {
      try {
        const importItem = await this.cms.importFromKulturNav({
          uuid: element.uuid,
          concept_type_id: this.kulturNavData.concept_type_id,
          superobject_filter_ids: this.selectedSuperobjectFilters,
          suppressErrHandler: true
        } as KulturNavImportParameters);
        if (importItem[AConst.ARTIFACT_ID]) {
          this.importedItems.push(importItem);
        } else {
          if (importItem && importItem.message) {
            this.errors.push(importItem.message);
          } else {
            this.errors.push('TRANS__CULTURE_HUB_SEARCH__IMPORT_FAILED');
          }
          break;
        }
        this.selection.deselect(element);
      } catch (e) {
        this.errors.push('TRANS__CULTURE_HUB_SEARCH__IMPORT_FAILED');
        this.errors.push(e.error?.message);
        break;
      }
    }
    if (!this.errors.length) {
      this.dialogRef.close(this.importedItems);
    }
    this.importing = false;
  }

  async runSearch(query: string) {
    if (query && this.validPhrase(query)) {
      this.hasSelections = false;
      this.noResults = false;
      this.searching = true;
      this.kulturNavConceptDataSource = [];
      const res = await this.cms.searchKulturNav({
        entity_type: this.kulturNavData.entity_type,
        concept_type_id: this.kulturNavData.concept_type_id,
        sub_type: this.selectedOption?.value,
        query: query,
        suppressErrHandler: true
      });
      this.searching = false;
      if (res.length) {
        this.kulturNavConceptDataSource = res.map(searchElement => {
          return {
            selected: false,
            conceptName: searchElement['artifact_name'],
            dataSet: searchElement['dataset'],
            uuid: searchElement['uuid']
          };
        });
      } else {
        this.kulturNavConceptDataSource = [];
        this.noResults = true;
      }
    }
  }

  closeImport() {
    this.dialogRef.close(this.importedItems);
  }

  onFilterChange(values: any[]) {
    this.selectedSuperobjectFilters = values;
  }

  private validPhrase(searchText: string) {
    const illegalCharacterErr = 'TRANS__CULTURE_HUB_SEARCH__ILLEGAL_CHARACTER_IN_QUERY';
    let res = true;
    const illegalCharacters = ['#', '%', '/', '\\', '?'];
    for (const illegalCharacter of illegalCharacters) {
      if (searchText.indexOf(illegalCharacter) !== -1) {
        res = false;
        break;
      }
    }
    const errExistPos = this.errors.indexOf(illegalCharacterErr);
    if (!res) {
      if (errExistPos === -1) {
        this.errors.push(illegalCharacterErr);
      }
    } else if (errExistPos !== -1) {
      this.errors.splice(errExistPos, 1);
    }
    return res;
  }

  findSelected(element: KulturNavConceptElement): KulturNavConceptElement {
    return this.selection.selected.find(selected => selected?.uuid === element?.uuid);
  }

  /**
   * Custom deselect method.
   * If multiple selection mode, we have to find an element from selection and use that to deselect.
   * This is to prevent cases where we refresh DOM and the element is treated as "element" !== "old.element".
   *
   * @param element
   */
  deselect(element: KulturNavConceptElement) {
    if (!this.selection.isMultipleSelection()) {
      this.selection.clear();
    } else {
      this.selection.deselect(this.findSelected(element))
    }
  }


  private cultureDataOptionsSort = (a: KulturNavOption, b: KulturNavOption) => {
    return a.label.localeCompare(b.label);
  }
}
